import useTranslate from "@app/_hooks/useTranslate";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@ui/dialog";
import { CountryCodeEnum } from "../config/i18n-config";
import { findMarketByCode, markets } from "../config/market-config";
import { useMarketStore } from "../hooks/useMarketStore";
import MarketSelectorItem from "./MarketSelectorItem";

const languageFormatter = new Map<string, Intl.DisplayNames>();

function getFormatter(locale: string, type: Intl.DisplayNamesOptions["type"]) {
  const formatterKey = `${locale}-${type}`;
  let formatter = languageFormatter.get(formatterKey);
  if (!formatter) {
    formatter = new Intl.DisplayNames(locale, {
      type,
    });
    languageFormatter.set(formatterKey, formatter);
  }
  return formatter;
}

const nordicMarketCodes = [
  CountryCodeEnum.SE,
  CountryCodeEnum.NO,
  CountryCodeEnum.DK,
  CountryCodeEnum.FI,
];

const nordics = Object.values(markets).filter((market) =>
  nordicMarketCodes.includes(market.countryCode),
);
const europe = Object.values(markets)
  .filter((market) => !nordicMarketCodes.includes(market.countryCode))
  .sort((a, b) => a.countryName.localeCompare(b.countryName));

export default function MarketSelectorDialog() {
  const t = useTranslate();
  const open = useMarketStore((state) => state.open);
  const market = useMarketStore((state) =>
    findMarketByCode(state.market ?? ""),
  );
  const setOpen = useMarketStore((state) => state.setOpen);
  const suggestion = useMarketStore((state) =>
    findMarketByCode(state.suggestedMarket ?? "", false),
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl">
        <DialogHeader className="flex-col items-start gap-y-2">
          <DialogTitle className="all-small-caps mx-auto max-w-xl text-center text-lg tracking-wider">
            {suggestion
              ? t("visitingStoreFrom", {
                  country: getFormatter(market.locale, "region").of(
                    suggestion.countryCode.toUpperCase(),
                  ),
                })
              : t("welcomeToNg")}
          </DialogTitle>
          <DialogDescription className="mx-auto max-w-xl text-center text-balance">
            {suggestion
              ? t("continueToLocalizedStore", {
                  language: getFormatter(market.locale, "language").of(
                    suggestion.languageCode.toUpperCase(),
                  ),
                })
              : `${t("selectYourCountry")}. ${t("unsupportedCountry")}`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid h-full w-full gap-x-2 gap-y-4 p-4 max-md:grid-flow-row md:grid-cols-3">
          <section className="col-span-1">
            <ul className="gap-2 md:columns-1">
              {nordics?.map((market) => (
                <MarketSelectorItem
                  key={market.countryCode}
                  market={market}
                  className="py-1"
                />
              ))}
            </ul>
          </section>
          <section className="col-span-1 md:col-span-2">
            <ul className="gap-2 md:columns-2">
              {europe?.map((market) => (
                <MarketSelectorItem
                  key={market.countryCode}
                  market={market}
                  className="py-1"
                />
              ))}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
